/**
 * Sector operations — Delivery Engagements, Allocations & Margin Service (R39 E1.a, SO-P105).
 * Typed reads, subscriptions, and callable wrappers for delivery engagements, finance, allocations, and rollups.
 */

import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type {
  OpsEngagement,
  OpsEngagementFinance,
  OpsAllocation,
  OpsMarginRollup,
  CreateEngagementInput,
  UpdateEngagementInput,
  SetEngagementStatusInput,
  SetEngagementMilestonesInput,
  SetEngagementFinanceInput,
  UpsertAllocationInput,
  EndAllocationInput,
  LinkGoalToEngagementInput,
  UnlinkGoalFromEngagementInput,
  RecomputeEngagementMarginInput,
  ExportDeliveryCsvInput,
  ExportDeliveryCsvResult,
  OpsMilestone,
  OpsEngagementStatus,
} from '../types/ops'

// ---------------------------------------------------------------------------
// Callables
// ---------------------------------------------------------------------------

export async function createEngagement(
  input: CreateEngagementInput,
): Promise<{ success: boolean; engagementId: string; opId?: string | null; replayed?: boolean }> {
  return callable<CreateEngagementInput, { success: boolean; engagementId: string; opId?: string | null; replayed?: boolean }>(
    'createEngagement',
    input,
  )
}

export async function updateEngagement(
  input: UpdateEngagementInput,
): Promise<{ success: boolean; engagementId: string; opId?: string | null; replayed?: boolean }> {
  return callable<UpdateEngagementInput, { success: boolean; engagementId: string; opId?: string | null; replayed?: boolean }>(
    'updateEngagement',
    input,
  )
}

export async function setEngagementStatus(
  input: SetEngagementStatusInput,
): Promise<{ success: boolean; engagementId: string; status: OpsEngagementStatus; opId?: string | null; replayed?: boolean }> {
  return callable<SetEngagementStatusInput, { success: boolean; engagementId: string; status: OpsEngagementStatus; opId?: string | null; replayed?: boolean }>(
    'setEngagementStatus',
    input,
  )
}

export async function setEngagementMilestones(
  input: SetEngagementMilestonesInput,
): Promise<{ success: boolean; engagementId: string; milestones: OpsMilestone[]; opId?: string | null; replayed?: boolean }> {
  return callable<SetEngagementMilestonesInput, { success: boolean; engagementId: string; milestones: OpsMilestone[]; opId?: string | null; replayed?: boolean }>(
    'setEngagementMilestones',
    input,
  )
}

export async function setEngagementFinance(
  input: SetEngagementFinanceInput,
): Promise<{ success: boolean; engagementId: string; opId?: string | null; replayed?: boolean }> {
  return callable<SetEngagementFinanceInput, { success: boolean; engagementId: string; opId?: string | null; replayed?: boolean }>(
    'setEngagementFinance',
    input,
  )
}

export async function upsertAllocation(
  input: UpsertAllocationInput,
): Promise<{ success: boolean; allocationId: string; opId?: string | null; replayed?: boolean }> {
  return callable<UpsertAllocationInput, { success: boolean; allocationId: string; opId?: string | null; replayed?: boolean }>(
    'upsertAllocation',
    input,
  )
}

export async function endAllocation(
  input: EndAllocationInput,
): Promise<{ success: boolean; allocationId: string; opId?: string | null; replayed?: boolean }> {
  return callable<EndAllocationInput, { success: boolean; allocationId: string; opId?: string | null; replayed?: boolean }>(
    'endAllocation',
    input,
  )
}

export async function linkGoalToEngagement(
  input: LinkGoalToEngagementInput,
): Promise<{ success: boolean; goalId: string; engagementId: string; clientId: string; opId?: string | null; replayed?: boolean }> {
  return callable<LinkGoalToEngagementInput, { success: boolean; goalId: string; engagementId: string; clientId: string; opId?: string | null; replayed?: boolean }>(
    'linkGoalToEngagement',
    input,
  )
}

export async function unlinkGoalFromEngagement(
  input: UnlinkGoalFromEngagementInput,
): Promise<{ success: boolean; goalId: string; engagementId: string; opId?: string | null; replayed?: boolean }> {
  return callable<UnlinkGoalFromEngagementInput, { success: boolean; goalId: string; engagementId: string; opId?: string | null; replayed?: boolean }>(
    'unlinkGoalFromEngagement',
    input,
  )
}

export async function recomputeEngagementMargin(
  input: RecomputeEngagementMarginInput,
): Promise<{ success: boolean; engagementId: string }> {
  return callable<RecomputeEngagementMarginInput, { success: boolean; engagementId: string }>(
    'recomputeEngagementMargin',
    input,
  )
}

export async function exportDeliveryCsv(
  input: ExportDeliveryCsvInput,
): Promise<ExportDeliveryCsvResult> {
  return callable<ExportDeliveryCsvInput, ExportDeliveryCsvResult>(
    'exportDeliveryCsv',
    input,
  )
}

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

export function subscribeOpsEngagements(
  workspaceId: string,
  onUpdate: (engagements: OpsEngagement[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_engagements')
  const q = query(
    coll,
    where('workspaceId', '==', workspaceId),
    orderBy('createdAt', 'desc'),
  )

  return onSnapshot(
    q,
    (snap) => {
      const list: OpsEngagement[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<OpsEngagement, 'id'>),
      }))
      onUpdate(list)
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}

export function subscribeOpsEngagement(
  engagementId: string,
  onUpdate: (engagement: OpsEngagement | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!engagementId) {
    onUpdate(null)
    return () => {}
  }

  const ref = doc(firestoreDb, 'ops_engagements', engagementId)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onUpdate(null)
        return
      }
      onUpdate({
        id: snap.id,
        ...(snap.data() as Omit<OpsEngagement, 'id'>),
      })
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}

export function subscribeOpsEngagementFinance(
  engagementId: string,
  onUpdate: (finance: OpsEngagementFinance | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!engagementId) {
    onUpdate(null)
    return () => {}
  }

  const ref = doc(firestoreDb, 'ops_engagement_finance', engagementId)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onUpdate(null)
        return
      }
      onUpdate({
        id: snap.id,
        ...(snap.data() as Omit<OpsEngagementFinance, 'id'>),
      })
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}

export function subscribeOpsAllocations(
  workspaceId: string,
  engagementId: string | null | undefined,
  onUpdate: (allocations: OpsAllocation[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_allocations')
  const q = engagementId
    ? query(
        coll,
        where('workspaceId', '==', workspaceId),
        where('engagementId', '==', engagementId),
        orderBy('from', 'asc'),
      )
    : query(coll, where('workspaceId', '==', workspaceId), orderBy('from', 'asc'))

  return onSnapshot(
    q,
    (snap) => {
      const list: OpsAllocation[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<OpsAllocation, 'id'>),
      }))
      onUpdate(list)
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}

export function subscribeOpsMarginRollup(
  rollupId: string,
  onUpdate: (rollup: OpsMarginRollup | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!rollupId) {
    onUpdate(null)
    return () => {}
  }

  const ref = doc(firestoreDb, 'ops_margin_rollups', rollupId)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onUpdate(null)
        return
      }
      onUpdate({
        id: snap.id,
        ...(snap.data() as Omit<OpsMarginRollup, 'id'>),
      })
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}

export function subscribeOpsMarginRollupsForWorkspace(
  workspaceId: string,
  onUpdate: (rollups: OpsMarginRollup[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_margin_rollups')
  const q = query(
    coll,
    where('workspaceId', '==', workspaceId),
    orderBy('computedAt', 'desc'),
  )

  return onSnapshot(
    q,
    (snap) => {
      const list: OpsMarginRollup[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<OpsMarginRollup, 'id'>),
      }))
      onUpdate(list)
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}
